import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../environments/environment';

export interface VersionRelease {
    version: string;
    title?: string;
    /** When set (e.g. "400", "300", "100"), UI renders this release as a bold go-live milestone. */
    goLiveDivision?: string;
    period: string;
    summary: string;
    features?: string[];
    enhancements?: string[];
    bugFixes?: string[];
}

export interface VersionHistoryManifest {
    generatedFrom?: string;
    note?: string;
    releases: VersionRelease[];
}

export interface RegisterBuildRequest {
    buildVersion: string;
    productVersion?: string;
    releaseNotes?: string;
    environment?: string;
    reportedBy?: string;
}

@Injectable({
    providedIn: 'root'
})
export class VersionHistoryService {
    private readonly historyUrl = '/assets/version-history.json';
    private readonly currentVersionUrl = '/assets/version.json';
    private readonly apiBase = environment.apiUrl.replace(/\/$/, '');

    constructor(private http: HttpClient) {}

    getReleases(): Observable<VersionRelease[]> {
        const apiUrl = `${this.apiBase}/version/releases`;
        return this.http.get<VersionRelease[]>(apiUrl).pipe(
            catchError((err) => {
                console.warn(`[VersionHistory] API failed (${apiUrl}), using JSON fallback`, err);
                return this.http
                    .get<VersionHistoryManifest>(this.historyUrl)
                    .pipe(map((manifest) => manifest.releases ?? []));
            })
        );
    }

    getCurrentVersion(): Observable<string> {
        return this.http.get<{ version: string }>(this.currentVersionUrl).pipe(map((res) => res.version));
    }

    registerBuild(buildVersion: string, productVersion?: string): Observable<void> {
        if (!buildVersion?.trim()) {
            return of(void 0);
        }

        const body: RegisterBuildRequest = {
            buildVersion: buildVersion.trim(),
            productVersion: productVersion?.trim() || undefined,
            environment: 'prod',
            reportedBy: 'ui-client'
        };

        const apiUrl = `${this.apiBase}/version/builds/register`;
        return this.http.post(apiUrl, body).pipe(
            map(() => void 0),
            catchError((err) => {
                console.warn(`[VersionHistory] Build register failed (${apiUrl})`, err);
                return of(void 0);
            })
        );
    }
}
